// NavNextChip — small standalone "Next →" pill rendered in the desktop
// navbar. Uses useNextStep to point at whichever stage the current user
// should hit next based on route + auth. Mirrors the bottom-of-page
// NextStepRail so the two surfaces are always in sync.
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useNextStep } from "@/hooks/useNextStep";
import AuthModal from "@/components/AuthModal";
import { trackFunnel } from "@/lib/funnelTracker";

const NavNextChip = () => {
  const next = useNextStep();
  const [authOpen, setAuthOpen] = useState(false);

  // Truncate long labels so the chip never breaks the navbar grid.
  const compactLabel = next.openAuth ? "Sign up to continue" : next.label;

  const onClick = (e: React.MouseEvent) => {
    trackFunnel("next_step_click", {
      surface: "nav_chip",
      step_id: next.id,
      open_auth: !!next.openAuth,
    });
    if (next.openAuth) {
      e.preventDefault();
      setAuthOpen(true);
    }
  };

  return (
    <>
      <Link
        to={next.href}
        onClick={onClick}
        title={`${next.eyebrow} — ${next.label}`}
        className="group hidden lg:inline-flex items-center gap-1.5 max-w-[220px] px-2.5 py-1 rounded-full border border-primary/40 bg-primary/10 text-[11px] font-mono font-semibold text-primary hover:bg-primary/20 hover:border-primary/60 transition-colors"
      >
        {/* Visible "Next" eyebrow doubles as the accessible-name prefix
            so axe/Lighthouse's label-content-name-mismatch (WCAG 2.5.3)
            stays satisfied — the previous aria-label hid "Next" from
            the accessible name. */}
        <span className="text-[9px] uppercase tracking-wider text-primary/70 shrink-0">
          Next
        </span>
        <span className="truncate">{compactLabel}</span>
        <ArrowRight className="w-3 h-3 shrink-0 group-hover:translate-x-0.5 transition-transform" />
      </Link>

      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} defaultMode="signup" />
    </>
  );
};

export default NavNextChip;